import { List, Map } from 'immutable';
import { combineActions, createAction, handleActions } from 'redux-actions';
import api from '../../utils/api';
import { normalizeImages } from './utils/helpers';

export const initialState = Map({
  isLoading: false,
  isLoadingMore: false,
  isDetailedLoading: false,
  isLoadingDetailedShipment: false,
  items: Map(), // Map { "YYYY-WW": List([ManifestSummary, ...]) }
  weekRanges: Map(), // Map { "YYYY-WW": "Nov 25 – Dec 1" }
  availableYears: List(), // List of years from API response
  manifestDetail: Map(), // Map { manifestId: ManifestDetail }
  fullDetailedConsolidation: Map(), // For ParcelDetailedPanel
  detailedShipment: Map(),
  tempDetailedItemId: null,
  totalPages: 1,
  currentPage: 0,
  hasMorePages: true,
});

// ── Action types ──────────────────────────────────────────────────────────────
export const GET_MANIFESTS = 'history/GET_MANIFESTS';
export const GET_MANIFEST_DETAIL = 'history/GET_MANIFEST_DETAIL';
export const GET_DETAILED_SHIPMENT = 'history/GET_DETAILED_SHIPMENT';
export const UPDATE_COMMENT = 'history/UPDATE_COMMENT';
export const GET_CUSTOMS_DECLARATIONS = 'history/GET_CUSTOMS_DECLARATIONS';
export const GET_HISTORY_FULL_DETAILED_ITEM =
  'history/GET_HISTORY_FULL_DETAILED_ITEM';

// ── Action creators ───────────────────────────────────────────────────────────
export const getManifests = createAction(
  GET_MANIFESTS,
  ({ append, ...params }) => ({
    promise: api.manifests.getManifests(params),
    data: { append, page: params.page },
  }),
);

export const getManifestDetail = createAction(
  GET_MANIFEST_DETAIL,
  api.manifests.getManifest,
);

export const getHistoryDetailedItem = createAction(
  GET_HISTORY_FULL_DETAILED_ITEM,
  api.history.getHistoryFullDetailedItem,
);

export const getDetailedShipment = createAction(
  GET_DETAILED_SHIPMENT,
  api.shipments.getShipment,
);

export const updateComment = createAction(
  UPDATE_COMMENT,
  api.shipments.updateComment,
);

export const getCustomsDeclarations = createAction(
  GET_CUSTOMS_DECLARATIONS,
  (data) => ({
    promise: api.outgoing.getCustomsDeclarations(data),
    data,
  }),
);

// ── Week grouping helpers ─────────────────────────────────────────────────────

/**
 * Returns { year, weekNum } based on ISO 8601 week definition (week starts Monday).
 */
const getISOWeekInfo = (dateStr) => {
  const date = new Date(dateStr);
  // Work in UTC to avoid timezone shifting the date
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
  const dayNum = d.getUTCDay() || 7; // Mon=1 … Sun=7
  // Move to nearest Thursday (ISO week-year ownership)
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNum = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return { year: d.getUTCFullYear(), weekNum };
};

/**
 * Returns "MMM D" for a UTC Date object.
 */
const fmtDate = (dt) =>
  dt.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });

/**
 * Given a created_at date string, returns the Mon–Sun week range label.
 */
const getWeekRange = (dateStr) => {
  const date = new Date(dateStr);
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
  const dayNum = d.getUTCDay() || 7;
  const monday = new Date(d);
  monday.setUTCDate(d.getUTCDate() - dayNum + 1);
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  return `${fmtDate(monday)} – ${fmtDate(sunday)}`;
};

/**
 * Groups an Immutable List of manifest summaries by ISO week.
 * Returns { items: Map { "YYYY-WW": List }, weekRanges: Map { "YYYY-WW": string } }
 */
const FALLBACK_WEEK_KEY = '0000-00';

const groupManifestsByWeek = (manifests) => {
  let items = Map();
  let weekRanges = Map();

  manifests.forEach((manifest) => {
    const createdAt = manifest.get('created_at');

    if (createdAt) {
      const { year, weekNum } = getISOWeekInfo(createdAt);
      const key = `${year}-${String(weekNum).padStart(2, '0')}`;
      items = items.update(key, (list = List()) => list.push(manifest));
      if (!weekRanges.has(key)) {
        weekRanges = weekRanges.set(key, getWeekRange(createdAt));
      }
    } else {
      // created_at not returned by BE - group into a fallback bucket
      items = items.update(FALLBACK_WEEK_KEY, (list = List()) =>
        list.push(manifest),
      );
      weekRanges = weekRanges.set(FALLBACK_WEEK_KEY, '');
    }
  });

  return { items, weekRanges };
};

/**
 * Merges new week-grouped manifests into existing state (for infinite scroll append).
 */
const mergeWeekGroups = (
  existingItems,
  existingRanges,
  newItems,
  newRanges,
) => {
  let merged = existingItems;
  newItems.forEach((manifests, weekKey) => {
    merged = merged.update(weekKey, (list = List()) => list.concat(manifests));
  });
  return { items: merged, weekRanges: existingRanges.merge(newRanges) };
};

// ── normalizeDetailed (unchanged, for ParcelDetailedPanel) ────────────────────
const normalizeImages_local = normalizeImages;

const getServiceRequestCount = (serviceRequests) =>
  serviceRequests.reduce((acc, request) => {
    const rawRequestType = request
      .getIn(['service_request_type', 'type'])
      .toLowerCase();
    const state = request.get('processed_state');
    return acc.set(rawRequestType, state);
  }, Map());

const normalizeDetailed = (parcel) => {
  const updatedParcel = parcel.updateIn(['items'], (items) =>
    items.map((item) =>
      item.set(
        'serviceRequestCount',
        getServiceRequestCount(item.get('service_requests')),
      ),
    ),
  );

  return updatedParcel
    .updateIn(['consolidation_addons'], (addons) =>
      addons.map((addon) => addon.get('id')),
    )
    .updateIn(['pictures'], normalizeImages_local)
    .setIn(['items'], updatedParcel.get('items'));
};

// ── Reducer ───────────────────────────────────────────────────────────────────
const historyReducer = handleActions(
  {
    [`${GET_MANIFESTS}_REQUEST`]: (state, { payload }) => {
      if (payload && payload.append) {
        return state.set('isLoadingMore', true);
      }
      return state.set('isLoading', true);
    },

    [`${GET_MANIFESTS}_SUCCESS`]: (state, { payload }) => {
      const manifests = payload.getIn(['data', 'manifests']);
      const years = payload.getIn(['data', 'years'], List());
      const headersRaw = Object.fromEntries(payload.get('headers').toJS());
      const totalPages =
        Math.ceil(+headersRaw['total'] / +headersRaw['per-page']) || 1;

      const { items: newItems, weekRanges: newRanges } =
        groupManifestsByWeek(manifests);

      const isAppending = state.get('isLoadingMore');
      const currentPage = +(
        headersRaw['current-page'] ||
        headersRaw['page'] ||
        1
      );

      let items, weekRanges;
      if (isAppending) {
        ({ items, weekRanges } = mergeWeekGroups(
          state.get('items'),
          state.get('weekRanges'),
          newItems,
          newRanges,
        ));
      } else {
        items = newItems;
        weekRanges = newRanges;
      }

      return state
        .set('items', items)
        .set('weekRanges', weekRanges)
        .set('availableYears', years)
        .set('totalPages', totalPages)
        .set('currentPage', currentPage)
        .set('hasMorePages', currentPage < totalPages)
        .set('isLoading', false)
        .set('isLoadingMore', false);
    },

    [`${GET_MANIFESTS}_ERROR`]: (state) =>
      state.set('isLoading', false).set('isLoadingMore', false),

    [`${GET_MANIFEST_DETAIL}_REQUEST`]: (state) =>
      state.set('isDetailedLoading', true),

    [`${GET_MANIFEST_DETAIL}_SUCCESS`]: (state, { payload }) => {
      const manifest = payload.getIn(['data', 'manifest']);
      const id = manifest.get('id');
      return state
        .setIn(['manifestDetail', id], manifest)
        .set('isDetailedLoading', false);
    },

    [`${GET_MANIFEST_DETAIL}_ERROR`]: (state) =>
      state.set('isDetailedLoading', false),

    /* ParcelDetailedPanel – full consolidation detail */
    [`${GET_HISTORY_FULL_DETAILED_ITEM}_REQUEST`]: (state) =>
      state.set('isDetailedLoading', true),

    [`${GET_HISTORY_FULL_DETAILED_ITEM}_SUCCESS`]: (state, { payload }) =>
      state
        .set(
          'fullDetailedConsolidation',
          normalizeDetailed(payload.get('data')),
        )
        .set('isDetailedLoading', false),

    [`${GET_HISTORY_FULL_DETAILED_ITEM}_ERROR`]: (state) =>
      state.set('isDetailedLoading', false),

    /* Custom declarations */
    [`${GET_CUSTOMS_DECLARATIONS}_REQUEST`]: (state, { payload }) =>
      state.set('tempDetailedItemId', payload).set('isDetailedLoading', true),

    [`${GET_CUSTOMS_DECLARATIONS}_SUCCESS`]: (state, { payload }) => {
      const itemId = state.get('tempDetailedItemId');
      return state
        .update('fullDetailedConsolidation', (item) =>
          item.get('id') === itemId
            ? item.setIn(['customs_declarations'], payload.get('data'))
            : item,
        )
        .set('isDetailedLoading', false);
    },

    /* Detailed shipment */
    [`${GET_DETAILED_SHIPMENT}_REQUEST`]: (state) =>
      state.set('isLoadingDetailedShipment', true),

    [`${GET_DETAILED_SHIPMENT}_SUCCESS`]: (state, { payload }) => {
      const data = payload.get('data');
      return state
        .setIn(['detailedShipment', data.get('id')], data)
        .set('isLoadingDetailedShipment', false);
    },

    [`${GET_DETAILED_SHIPMENT}_ERROR`]: (state) =>
      state.set('isLoadingDetailedShipment', false),

    [`${UPDATE_COMMENT}_SUCCESS`]: (state, { payload }) => {
      const id = payload.getIn(['data', 'id']);
      const comment = payload.getIn(['data', 'customer_comment']);
      return state.setIn(['detailedShipment', id, 'customer_comment'], comment);
    },
  },
  initialState,
);

export default historyReducer;
